import React, { useState, useEffect } from 'react';
import { Plus, Layers, Sparkles, X, TrendingUp, ShoppingBag, Zap, Lightbulb, ChevronRight, Loader2, Bot, Shirt, Coffee, Gift, Heart, Star } from 'lucide-react';
import toast from 'react-hot-toast';
import { api, Bundle, BundleSuggestion } from '../services/api';

import BundleGrid from './BundleGrid';
import BundleForm from './BundleForm';
import DeleteBundleConfirmModal from './DeleteBundleConfirmModal';

const suggestionIcons = [TrendingUp, ShoppingBag, Zap, Lightbulb, Gift, Heart, Star, Coffee];

export default function BundleSelector() {
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingBundle, setEditingBundle] = useState<Bundle | null>(null);
  const [bundleToDelete, setBundleToDelete] = useState<Bundle | null>(null);
  const [loading, setLoading] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<BundleSuggestion[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSource, setAiSource] = useState('');

  const loadBundles = async () => {
    try {
      const data = await api.bundles.getAll();
      setBundles(data.filter(b => b.isActive && !b.isArchived));
    } catch (err) {
      toast.error('Erro ao carregar pacotes');
    }
  };

  useEffect(() => { loadBundles(); }, []);

  const handleOpenNew = () => {
    setEditingBundle(null);
    setIsFormOpen(true);
  };

  const handleEdit = (bundle: Bundle) => {
    setEditingBundle(bundle);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingBundle(null);
  };

  const handleFormSuccess = () => {
    loadBundles();
    handleCloseForm();
    toast.success(editingBundle ? 'Pacote atualizado!' : 'Pacote criado!');
  };

  const handleRequestDelete = (bundle: Bundle) => {
    setBundleToDelete(bundle);
  };

  const handleCancelDelete = () => setBundleToDelete(null);

  const handleConfirmDelete = async () => {
    if (!bundleToDelete) return;
    setLoading(true);
    try {
      await api.bundles.delete(bundleToDelete._id);
      toast.success('Pacote eliminado');
      loadBundles();
    } catch (err) {
      toast.error('Erro ao eliminar pacote');
    } finally {
      setLoading(false);
      setBundleToDelete(null);
    }
  };

  const handleAISuggest = async () => {
    setAiLoading(true);
    setShowAIModal(true);
    setAiSuggestions([]);
    try {
      const res = await api.bundles.suggest();
      setAiSuggestions(res.suggestions);
      setAiSource(res.source);
      if (res.suggestions.length === 0) {
        toast.error('Nenhuma sugestão disponível. Adicione produtos primeiro.');
      }
    } catch (err) {
      toast.error('Erro ao gerar sugestões');
      setAiSuggestions([]);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Layers className="w-6 h-6 text-purple-600" />
          <div>
            <h1 className="text-xl md:text-2xl font-black text-gray-900 uppercase tracking-tight">
              Combos & Subscrições
            </h1>
            <p className="text-xs text-gray-500">Pacotes, planos e combos inteligentes</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleAISuggest}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-5 py-2.5 rounded-xl flex items-center gap-2 font-medium shadow-lg shadow-purple-900/20 transition-all active:scale-95 text-sm"
          >
            <Sparkles size={16} />
            <span className="hidden sm:inline">Sugestão IA</span>
            <span className="sm:hidden">IA</span>
          </button>
          <button
            onClick={handleOpenNew}
            className="bg-purple-600 hover:bg-purple-700 text-white px-5 py-2.5 rounded-xl flex items-center gap-2 font-medium shadow-md transition text-sm"
          >
            <Plus size={18} />
            <span className="hidden sm:inline">Novo Pacote</span>
            <span className="sm:hidden">Novo</span>
          </button>
        </div>
      </div>

      {/* Grid */}
      <BundleGrid bundles={bundles} onEdit={handleEdit} onDelete={handleRequestDelete} />

      {/* AI Suggestion Modal */}
      {showAIModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl">
                  {aiLoading ? (
                    <Loader2 size={20} className="text-white animate-spin" />
                  ) : (
                    <Bot size={20} className="text-white" />
                  )}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Sugestões Inteligentes</h2>
                  {aiSource && !aiLoading && (
                    <p className="text-[10px] text-gray-500 font-medium">Fonte: {aiSource}</p>
                  )}
                </div>
              </div>
              <button onClick={() => setShowAIModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition">
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {aiLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 size={40} className="text-purple-600 animate-spin mb-4" />
                  <p className="text-gray-600 font-medium">A analisar produtos e vendas...</p>
                  <p className="text-sm text-gray-400 mt-1">A IA está a gerar as melhores combinações para o seu negócio</p>
                </div>
              ) : aiSuggestions.length === 0 ? (
                <div className="text-center py-16 text-gray-500">
                  <Lightbulb className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                  <p className="font-bold">Nenhuma sugestão disponível</p>
                  <p className="text-sm">Cadastre produtos e serviços primeiro.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {aiSuggestions.map((suggestion, idx) => {
                    const Icon = suggestionIcons[idx % suggestionIcons.length];
                    return (
                      <div key={idx} className="border border-gray-100 rounded-xl overflow-hidden hover:shadow-md transition">
                        {/* Type badge */}
                        <div className="px-4 py-2 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-gray-100 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon size={14} className="text-purple-600" />
                            <span className="text-[10px] font-bold text-purple-700 uppercase tracking-wider">
                              {suggestion.type === 'ai' ? 'Sugestão IA' : suggestion.type === 'data-driven' ? 'Baseado em Vendas' : 'Estratégia'}
                            </span>
                          </div>
                          {suggestion.suggestedPrice && suggestion.originalPrice && (
                            <span className="text-[10px] font-bold text-green-600">
                              -{Math.round((1 - suggestion.suggestedPrice / suggestion.originalPrice) * 100)}%
                            </span>
                          )}
                        </div>

                        <div className="p-4">
                          {/* AI content renders as markdown-like text */}
                          {suggestion.content ? (
                            <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                              {suggestion.content}
                            </div>
                          ) : (
                            <>
                              <h3 className="font-bold text-gray-900 mb-2">{suggestion.name}</h3>
                              {suggestion.items && suggestion.items.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-3">
                                  {suggestion.items.map((item, i) => (
                                    <span key={i} className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium border border-purple-100">
                                      {item}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {suggestion.originalPrice && suggestion.suggestedPrice && (
                                <div className="flex items-center gap-4 mb-3">
                                  <div>
                                    <p className="text-[10px] text-gray-400 uppercase font-bold">Preço Original</p>
                                    <p className="text-sm font-bold text-gray-500 line-through">{suggestion.originalPrice.toLocaleString('pt-MZ')} MT</p>
                                  </div>
                                  <ChevronRight size={16} className="text-gray-300" />
                                  <div>
                                    <p className="text-[10px] text-gray-400 uppercase font-bold">Preço Sugerido</p>
                                    <p className="text-lg font-black text-purple-700">{suggestion.suggestedPrice.toLocaleString('pt-MZ')} MT</p>
                                  </div>
                                </div>
                              )}
                              {suggestion.reason && (
                                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                                  <p className="text-xs text-gray-600">
                                    <Lightbulb size={12} className="inline text-amber-500 mr-1" />
                                    {suggestion.reason}
                                  </p>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setShowAIModal(false)}
                className="px-6 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition text-sm"
              >
                {aiSuggestions.length > 0 ? 'Criar Combos Manualmente' : 'Fechar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      {isFormOpen && (
        <BundleForm initialBundle={editingBundle} onSuccess={handleFormSuccess} onCancel={handleCloseForm} />
      )}

      {/* Delete confirm */}
      {bundleToDelete && (
        <DeleteBundleConfirmModal
          bundle={bundleToDelete}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          isDeleting={loading}
        />
      )}
    </div>
  );
}
